<script lang="ts">
  type Feature = {
    icon: string;
    iconClass: string;
    title: string;
    description: string;
  };

  const features: Feature[] = [
    {
      icon: '🔒',
      iconClass: 'icon-privacy',
      title: '100% Privacy & Data Control',
      description: 'Your financial data stays in your browser. No cloud sync, no telemetry, no data collection. Export anytime as SQL backup. Your data, your rules, no lock-in.',
    },
    {
      icon: '⚡',
      iconClass: 'icon-ai',
      title: 'No Setup Required',
      description: 'Works instantly in your browser. No account creation, no downloads, no configuration. Import CSVs and start budgeting in under a minute.',
    },
    {
      icon: '🤖',
      iconClass: 'icon-ai',
      title: 'Smart Auto-Categorization',
      description: 'Automatic matching with fuzzy name detection and smart rules. Connect your own AI (Anthropic Claude or OpenAI GPT) for bulk categorization of remaining transactions. You control the model and cost.',
    },
    {
      icon: '🏦',
      iconClass: 'icon-open-source',
      title: 'Multi-Bank Import',
      description: 'Download CSVs from your banks quarterly and import them all at once. Supports Chase, Bank of America, Amex, US Bank, SoFi, BMO, and more with automatic format detection.',
    },
    {
      icon: '👨‍👩‍👧‍👦',
      iconClass: 'icon-family',
      title: 'Multi-Account & Person Tracking',
      description: 'Import transactions from multiple bank accounts and credit cards. Organize and track spending by person to understand household spending patterns at a glance.',
    },
    {
      icon: '💰',
      iconClass: 'icon-success',
      title: 'Budget Management',
      description: 'Set category budgets with automatic rollover. Get alerts when approaching limits. See budget vs actual spending at a glance.',
    },
    {
      icon: '📈',
      iconClass: 'icon-ai',
      title: 'Spending Trends & Insights',
      description: 'See how your spending patterns change over time. Compare quarters and years to understand where your money is really going.',
    },
    {
      icon: '🔄',
      iconClass: 'icon-privacy',
      title: 'Recurring Detection',
      description: 'Automatically finds subscriptions and bills with confidence scoring. Stop paying for subscriptions you forgot about.',
    },
  ];
</script>

<section class="section features-section">
  <div class="container">
    <div class="section-header">
      <h2>Not for Daily Budgeting. Built for Thoughtful Review.</h2>
      <p>Everything you need for monthly, quarterly, and yearly budget check-ins</p>
    </div>
    <div class="features-grid">
      {#each features as feature}
        <div class="feature-card card">
          <div class="icon-wrapper {feature.iconClass}">
            <span class="feature-icon">{feature.icon}</span>
          </div>
          <h3>{feature.title}</h3>
          <p>{feature.description}</p>
        </div>
      {/each}
    </div>
  </div>
</section>

<style>
  .features-section {
    /* Background controlled by parent Landing.svelte */
  }

  .section-header {
    text-align: center;
    max-width: 700px;
    margin: 0 auto var(--spacing-2xl);
  }

  .section-header h2 {
    font-size: var(--font-size-3xl);
    margin-bottom: var(--spacing-sm);
  }

  .section-header p {
    font-size: var(--font-size-lg);
    color: var(--color-text-secondary);
    margin: 0;
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: var(--spacing-lg);
  }

  .feature-card {
    text-align: left;
    padding: var(--spacing-lg);
    border: 2px solid transparent;
    transition: all var(--transition-base);
  }

  .feature-card .icon-wrapper {
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  }

  .feature-card:hover {
    border-color: var(--color-border);
    transform: translateY(-4px);
  }

  .icon-wrapper {
    width: 56px;
    height: 56px;
    border-radius: var(--radius-lg);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: var(--spacing-md);
  }

  .feature-card h3 {
    font-size: var(--font-size-lg);
    margin-bottom: var(--spacing-sm);
  }

  .feature-card p {
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
    font-size: var(--font-size-sm);
    margin: 0;
  }

  .feature-icon {
    font-size: var(--font-size-2xl);
  }

  @media (max-width: 768px) {
    .section-header h2 {
      font-size: var(--font-size-2xl);
    }

    .features-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
